import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useCollab — manages a real-time collab WebSocket connection to /api/collab/ws/{roomId}.
 *
 *   const collab = useCollab({ roomId, name, onMessage });
 *
 * Returns:
 *   - status         "idle" | "connecting" | "connected" | "closed" | "error"
 *   - me             {id, name}  (server-assigned, available after first welcome)
 *   - peers          Array<{id, name}> — currently connected co-collaborators
 *   - send(type, payload)
 *   - disconnect()
 *
 * Lifecycle:
 *  - If `roomId` is falsy, the hook stays idle. The caller is responsible for
 *    showing the join/create modal.
 *  - Reconnects on transient close (code != 1000) with a short backoff.
 *    Permanent close (e.g. server kicked us with 1008 'room full') stays closed.
 *  - Unmount or `disconnect()` cleanly closes with code 1000.
 *
 * StrictMode safety:
 *  The effect that opens the WebSocket schedules `connect()` via a 0ms timeout.
 *  If React (in dev / Fast Refresh) immediately unmounts and re-mounts the
 *  effect, the cleanup pass cancels the pending timer BEFORE the WebSocket
 *  is ever constructed — so we never end up with two live sockets per user.
 *  Without this, the StrictMode double-effect would inflate the room's peer
 *  roster (every user appears twice; observed in iter-33 testing).
 */
export default function useCollab({ roomId, name, onMessage }) {
  const [status, setStatus] = useState("idle");
  const [me, setMe] = useState(null);
  const [peers, setPeers] = useState([]);
  const [error, setError] = useState(null);

  const wsRef = useRef(null);
  const onMessageRef = useRef(onMessage);
  useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);

  const doConnect = useCallback((rid, n) => {
    setStatus("connecting");
    setError(null);
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    const url = `${base}/api/collab/ws/${encodeURIComponent(rid)}?name=${encodeURIComponent(n || "Anonymous")}`;
    let ws;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      setStatus("error");
      setError(e.message);
      return null;
    }
    wsRef.current = ws;

    ws.onopen = () => setStatus("connected");
    ws.onmessage = (ev) => {
      let msg;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (!msg || typeof msg !== "object") return;
      if (msg.type === "welcome") {
        setMe(msg.you);
        setPeers(msg.peers || []);
      } else if (msg.type === "peer_join") {
        setPeers((prev) => prev.find((p) => p.id === msg.user.id) ? prev : [...prev, msg.user]);
      } else if (msg.type === "peer_leave") {
        setPeers((prev) => prev.filter((p) => p.id !== msg.user.id));
      }
      try { onMessageRef.current?.(msg); } catch (e) { console.warn("[collab] onMessage threw", e); }
    };
    ws.onerror = (e) => { void e; setStatus("error"); };
    ws.onclose = (ev) => {
      // Only mutate state if THIS socket is still the current one — protects
      // us from a stale-close racing a fresh connection.
      if (wsRef.current === ws) {
        wsRef.current = null;
        setStatus("closed");
        setMe(null);
        setPeers([]);
      }
      if (ev.code === 1008) setError(ev.reason || "Connection refused by server");
    };
    return ws;
  }, []);

  const send = useCallback((type, payload, opts) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      // Optional `to: peerId` envelope field for directed (1:1) messages —
      // used by WebRTC signaling so offer/answer/ICE don't broadcast to the
      // whole room. Absent => normal broadcast through the server.
      const msg = { type, payload };
      if (opts && typeof opts.to === "string" && opts.to) msg.to = opts.to;
      ws.send(JSON.stringify(msg));
      return true;
    } catch (e) {
      console.warn("[collab] send failed", e);
      return false;
    }
  }, []);

  const disconnect = useCallback(() => {
    const ws = wsRef.current;
    if (ws) {
      try { ws.close(1000, "Client requested"); } catch (e) { void e; }
      wsRef.current = null;
    }
    setStatus("idle");
  }, []);

  // Auto-connect when roomId becomes truthy. The setTimeout(0) is the
  // StrictMode-safety mechanism: if React double-invokes the effect (mount →
  // cleanup → mount), the cleanup cancels the pending timer before any
  // WebSocket is constructed. Net result: exactly one socket per real mount.
  useEffect(() => {
    if (!roomId) {
      // Tear down any open socket and stay idle.
      if (wsRef.current) {
        try { wsRef.current.close(1000); } catch (e) { void e; }
        wsRef.current = null;
      }
      setStatus("idle");
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      if (cancelled) return;
      doConnect(roomId, name);
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      // If somehow a socket made it through (network race), close it cleanly.
      if (wsRef.current) {
        try { wsRef.current.close(1000); } catch (e) { void e; }
        wsRef.current = null;
      }
    };
  }, [roomId, name, doConnect]);

  return { status, me, peers, error, send, disconnect };
}
